/* eslint-disable @typescript-eslint/no-empty-function */
import { FormHandles } from '@unform/core'
import { Form } from '@unform/web'
import { Body2 } from 'stories/elements/Typography/Text'
import Stack from 'components/elements/Stack'
import { Grade } from 'interfaces/Grade'
import { SchoolType } from 'interfaces/SchoolType'
import { AcademicTier } from 'interfaces/University'
import theme from 'stories/utils/theme'
import { uniqueBy } from 'lib/formatting/grades'
import _useTranslation from 'lib/hooks/_useTranslation'
import { useEffect, useRef, useState } from 'react'
import { ApiError } from 'services/ApiService'
import GradeService from 'services/GradeService'
import styled from 'styled-components'
import Select from 'components/elements/Inputs/Select'

const GradesGrid = styled.div<{ itemsPerCol: number }>`
    display: grid;
    grid-template-columns: ${({ itemsPerCol }) => `repeat(${itemsPerCol}, 1fr)`};
    gap: 10px;
    @media (max-width: 1050px) {
        grid-template-columns: ${({ itemsPerCol }) => `repeat(${itemsPerCol - 1}, 1fr)`};
    }
`

const GradeItem = styled.div<{ isSelected: boolean; isDarkTheme?: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border-radius: ${theme.borderRadius.normal};
    background-color: ${({ isSelected, isDarkTheme }) =>
        isSelected ? theme.colors.deepSeaBlue : isDarkTheme ? theme.colors.backgroundTransparent : theme.colors.lightBorder};
    height: 48px;
    min-width: 48px;
    p {
        color: ${({ isSelected, isDarkTheme }) =>
            isSelected ? theme.colors.white : isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.subtitleBlack};
        text-align: center;
    }
`

interface Props {
    toggleGrade: (grade: Grade) => void
    selectedSchoolTypes: SchoolType[] | undefined
    selectedGrades: Grade[]
    limitToOneSelectedItem?: boolean
    isDarkTheme?: boolean
}

const GradeSelectionGrades = ({
    selectedSchoolTypes,
    selectedGrades,
    toggleGrade,
    limitToOneSelectedItem,
    isDarkTheme,
}: Props) => {
    const [grades, setGrades] = useState<Grade[]>([])
    const { t: tCommon } = _useTranslation('common')
    const { t } = _useTranslation('knows')

    const limitedFormRef = useRef<FormHandles>(null)

    const selectedGradeIds = selectedGrades.sort((a, b) => a.level - b.level).map((g) => g.id)

    const isUniversitySelected = selectedSchoolTypes?.some(
        (schoolType) => schoolType.academicTier === AcademicTier.University
    )

    useEffect(() => {
        async function fetchGradesBySchoolType(schoolType: SchoolType[]) {
            const promises: Promise<void>[] = []
            let gradesOptions: Grade[] = []
            schoolType.forEach((school) => {
                const promise = GradeService.listGradesBySchoolType(school.id)
                    .then((grades) => {
                        gradesOptions = [...gradesOptions, ...grades]
                    })
                    .catch((error) => {
                        if (error instanceof ApiError) {
                            error.handleUnknownError(tCommon, 'schoolTypes.list')
                        } else {
                            throw error
                        }
                    })

                promises.push(promise)
            })
            Promise.all(promises).then(() => {
                const uniqueGrades = uniqueBy('id', gradesOptions).sort((a, b) => a.level - b.level)
                setGrades(uniqueGrades)

                // currently grade doesn't have academicTier, so we need to hardcode this check
                if (isUniversitySelected && uniqueGrades.length > 0) {
                    const universityGrade = uniqueGrades.find(
                        (grade) => grade.shortName === 'Universität' || grade.longName === 'Universität'
                    )
                    if (universityGrade && !selectedGradeIds.includes(universityGrade.id)) {
                        toggleGrade(universityGrade)
                    }
                }
            })
        }

        if (selectedSchoolTypes && selectedSchoolTypes?.length > 0) {
            fetchGradesBySchoolType(selectedSchoolTypes)
        } else {
            setGrades([])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSchoolTypes, tCommon])

    if (!grades) return null

    if (isUniversitySelected) {
        return null
    }

    const generateGradeOption = (grades: Grade[]) => [
        ...grades.map((grade) => ({
            label: grade.longName,
            data: grade,
        })),
    ]

    const intialLimitedGradeData = {
        label: selectedGrades[0]?.longName,
        data: selectedGrades[0],
    }

    return (
        <Stack>
            <GradesGrid itemsPerCol={limitToOneSelectedItem ? 1 : 4}>
                {limitToOneSelectedItem ? (
                    <Form ref={limitedFormRef} onSubmit={() => {}} initialData={{ grades: intialLimitedGradeData }}>
                        <Select
                            name="grades"
                            options={generateGradeOption(grades)}
                            label={t('knows/createDetailsPageKnowGradeLabel')}
                            placeholder={t('knows/createDetailsPageKnowGradeLabel')}
                            onUpdate={toggleGrade}
                            noMargin={false}
                            darkTheme={isDarkTheme}
                        />
                    </Form>
                ) : (
                    grades.map((grade) => (
                        <GradeItem
                            key={grade.id}
                            onClick={() => toggleGrade(grade)}
                            isSelected={selectedGradeIds.includes(grade.id)}
                            isDarkTheme={isDarkTheme}
                        >
                            <Body2>{grade.longName}</Body2>
                        </GradeItem>
                    ))
                )}
            </GradesGrid>
        </Stack>
    )
}

export default GradeSelectionGrades
